import { useLayoutEffect, useRef } from 'react';
import { applyTenantTheme, removeTenantTheme, loadTenantFont } from '@glomopay/theme/tenant';
import type { TenantBranding } from '@glomopay/tenant';

const DEFAULT_GRADIENT_ANGLE = 45;

// Applies tenant branding (brand colour ramp + optional gradient fill + font) at
// the layout root. Empty primaryColor (the Glomo default) is skipped so the
// central --brand-primary token wins. Cleans up the CSS overrides on unmount.
export const useTenantTheme = (branding: TenantBranding): void => {
  const appliedRef = useRef(false);

  const primaryColor = branding.theme.primaryColor.trim();
  const { fontFamily, fontsCssUrl } = branding;

  // Read the stops as primitives rather than depending on the gradient object:
  // a fresh object arrives with every tenant fetch and would re-apply the theme
  // on each render.
  const gradientFrom = branding.theme.brandGradient?.from ?? '';
  const gradientTo = branding.theme.brandGradient?.to ?? '';
  const gradientAngle = branding.theme.brandGradient?.angle ?? DEFAULT_GRADIENT_ANGLE;

  useLayoutEffect(() => {
    const brandGradient = gradientFrom && gradientTo ? { from: gradientFrom, to: gradientTo, angle: gradientAngle } : undefined;

    // Called before the empty-seed check so applyTenantTheme owns the decision: it
    // no-ops without a seed, and reports a gradient that had to be dropped.
    applyTenantTheme({ primaryColor, fontFamily, brandGradient });
    if (!primaryColor) return;

    appliedRef.current = true;

    if (fontsCssUrl) {
      loadTenantFont(fontsCssUrl).catch(() => {
        // Font load failure is non-critical — the portal stays functional with the system font.
      });
    }

    return () => {
      if (appliedRef.current) {
        removeTenantTheme();
        appliedRef.current = false;
      }
    };
  }, [primaryColor, fontFamily, fontsCssUrl, gradientFrom, gradientTo, gradientAngle]);
};
